// @flow

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { hasTrial, getTrialDays, getTrialCTAText } from '../../utils/trialUtils';
import { getDisplayPrice } from '../../utils/pricingUtils';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Link from '@mui/material/Link';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { useTheme } from '@mui/material/styles';

type Plan = {
  product: Object,
  prices: {
    monthly: ?Object,
    yearly: ?Object
  }
};

type Props = {
  plans: Array<Plan>,
  onSelectPlan: (product: Object, monthlyPrice: ?Object, yearlyPrice: ?Object, isYearly: boolean) => void
};

/**
 * Normalize prices by inspecting price.recurring.interval instead of trusting key names.
 * This handles cases where backend returns prices under the wrong keys.
 */
const getActualMonthlyPrice = (prices: { monthly: ?Object, yearly: ?Object }): ?Object => {
  if (prices.monthly?.recurring?.interval === 'month') return prices.monthly;
  if (prices.yearly?.recurring?.interval === 'month') return prices.yearly;
  return prices.monthly; // fallback
};

const getActualYearlyPrice = (prices: { monthly: ?Object, yearly: ?Object }): ?Object => {
  if (prices.yearly?.recurring?.interval === 'year') return prices.yearly;
  if (prices.monthly?.recurring?.interval === 'year') return prices.monthly;
  return prices.yearly; // fallback
};

/**
 * Calculate yearly savings percentage for a plan.
 */
const calculateYearlySavings = (monthlyPrice: ?Object, yearlyPrice: ?Object): ?number => {
  if (!monthlyPrice || !yearlyPrice) return null;
  const monthlyAmount = monthlyPrice.unit_amount || 0;
  const yearlyAmount = yearlyPrice.unit_amount || 0;
  if (monthlyAmount === 0) return null;
  const twelveMonthsCost = monthlyAmount * 12;
  const savingsPercent = ((twelveMonthsCost - yearlyAmount) / twelveMonthsCost) * 100;
  return savingsPercent > 0 ? Math.round(savingsPercent) : null;
};

/**
 * Format price for display.
 * Returns formatted string like "$29" or "$29.50"
 */
const formatPrice = (unitAmount: number): string => {
  const dollars = unitAmount / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};

/**
 * Get monthly equivalent price for display.
 * For yearly prices, divides by 12.
 */
const getMonthlyEquivalent = (price: ?Object, isYearly: boolean): string => {
  if (!price) return '$0';
  const amount = price.unit_amount || 0;
  if (isYearly) {
    const monthly = amount / 12;
    return monthly % 100 === 0 ? `$${monthly / 100}` : `$${(monthly / 100).toFixed(2)}`;
  }
  return formatPrice(amount);
};

function PlanPickerStep(props: Props) {
  const { plans, onSelectPlan } = props;
  const theme = useTheme();
  const [isYearly, setIsYearly] = useState(false);

  // Check if toggle should be shown: at least one plan has both monthly AND yearly prices
  const hasMultipleIntervals = plans.some((plan) => {
    const monthly = getActualMonthlyPrice(plan.prices);
    const yearly = getActualYearlyPrice(plan.prices);
    return monthly && yearly;
  });

  return (
    <Box>
      <Helmet>
        <title>Neo | Choose Your Plan</title>
      </Helmet>

      {/* Monthly/Yearly Toggle — only show if plans have both intervals */}
      {hasMultipleIntervals && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: !isYearly ? 600 : 400, mr: 1 }}
          >
            {'Monthly'}
          </Typography>
          <Switch
            checked={isYearly}
            onChange={(e) => setIsYearly(e.target.checked)}
            color="primary"
            inputProps={{ 'aria-label': 'Toggle billing period' }}
          />
          <Typography
            variant="body1"
            sx={{ fontWeight: isYearly ? 600 : 400, ml: 1 }}
          >
            {'Yearly'}
          </Typography>
        </Box>
      )}

      {/* Plan Cards */}
      <Grid container spacing={3} justifyContent="center">
        {plans.map((plan) => {
          const { product, prices } = plan;
          const monthlyPrice = getActualMonthlyPrice(prices);
          const yearlyPrice = getActualYearlyPrice(prices);
          const activePrice = isYearly ? yearlyPrice : monthlyPrice;
          const trialDays = getTrialDays(product);
          const isTrialPlan = hasTrial(product);
          const isRecommended = product.metadata?.recommended === 'true';
          const yearlySavings = calculateYearlySavings(monthlyPrice, yearlyPrice);
          const ctaText = getTrialCTAText(product);
          const hasBadges = isRecommended || isTrialPlan || (isYearly && yearlySavings);

          return (
            <Grid item xs={12} md={4} key={product.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: isRecommended
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: '8px',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, pt: 3 }}>
                  {/* Badges Row — only render if there's at least one badge */}
                  {hasBadges && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {isRecommended && (
                        <Chip
                          label="Most Popular"
                          color="primary"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                      {isTrialPlan && (
                        <Chip
                          label={`${trialDays}-day free trial`}
                          color="success"
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, color: 'success.main' }}
                          data-testid="trial-badge"
                        />
                      )}
                      {isYearly && yearlySavings && (
                        <Chip
                          label={`Save ${yearlySavings}%`}
                          color="warning"
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, color: 'warning.main' }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Plan Name */}
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {product.name}
                  </Typography>

                  {/* Price */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {getMonthlyEquivalent(activePrice, isYearly)}
                      <Typography
                        component="span"
                        variant="body1"
                        sx={{ fontWeight: 400, color: 'text.secondary' }}
                      >
                        {'/user/mo'}
                      </Typography>
                    </Typography>
                    {isYearly && (
                      <Typography variant="body2" color="text.secondary">
                        {`${formatPrice(activePrice?.unit_amount || 0)} billed yearly`}
                      </Typography>
                    )}
                    {isTrialPlan && (
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                        {'after free trial'}
                      </Typography>
                    )}
                  </Box>

                  {/* Description */}
                  {product.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {product.description}
                    </Typography>
                  )}

                  {/* Features List */}
                  {product.marketing_features && product.marketing_features.length > 0 && (
                    <Box>
                      {product.marketing_features.map((feature, idx) => (
                        <Box
                          key={idx}
                          sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75 }}
                        >
                          <CheckIcon
                            sx={{
                              fontSize: 18,
                              color: 'success.main',
                              mr: 1,
                              mt: 0.3,
                              flexShrink: 0
                            }}
                          />
                          <Typography variant="body2">
                            {feature.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant={isRecommended ? 'contained' : 'outlined'}
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={() => onSelectPlan(product, monthlyPrice, yearlyPrice, isYearly)}
                    sx={{ fontWeight: 600, py: 1.5, borderRadius: '8px' }}
                  >
                    {ctaText}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3, mb: 4 }}>
        <Link href="https://askneo.io/pricing/" target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          Compare plans and pricing
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </Link>
      </Typography>
    </Box>
  );
}

export { getActualMonthlyPrice, getActualYearlyPrice };
export default PlanPickerStep;
